import React from 'react';
import { Play, Save, Bug, Wand2, Settings, Terminal, Undo2, Redo2 } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip.js';



interface TopBarProps {
  fileName?: string;
  onRun?: () => void;
  onSave?: () => void;
  onDebug?: () => void;
  onFixWithARCOS?: () => void;
  onTerminalToggle?: () => void;
  layoutMode?: 'classic' | 'hive-grid';
  onLayoutToggle?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

interface IconButtonProps {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  shortcut?: string;
}

function IconButton({ icon: Icon, label, onClick, disabled = false, shortcut }: IconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          aria-label={label}
          className="p-2 hover:bg-slate-800/50 rounded disabled:opacity-40 transition-colors"
          onClick={onClick}
          disabled={disabled}
        >
          <Icon size={16} />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {label} {shortcut && `(${shortcut})`}
      </TooltipContent>
    </Tooltip>
  );
}

const TopBar: React.FC<TopBarProps> = ({
  fileName = 'Code Editor',
  onRun,
  onSave,
  onDebug,
  onFixWithARCOS,
  onTerminalToggle,
  layoutMode = 'classic',
  onLayoutToggle,
  onUndo,
  onRedo
}) => {
  return (
    <TooltipProvider>
      <div className="flex items-center justify-between p-3 border-b border-white/10 bg-slate-900/60 backdrop-blur-xl arc-glass-panel">
        <div className="flex items-center space-x-3">
          <h3 
            className="text-lg font-semibold drop-shadow-md"
            style={{
              background: `linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0.4) 100%), rgb(86, 156, 214)`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              textShadow: '0 1px 2px rgba(0,0,0,0.4)',
              display: 'inline-block',
              whiteSpace: 'nowrap'
            }}
          >
            {fileName}
          </h3>
        </div>
        
        <div className="flex items-center space-x-1">
            <IconButton 
              icon={Play} 
              label="Run" 
              onClick={onRun}
              shortcut="⌘R"
            />
            <IconButton 
              icon={Save} 
              label="Save" 
              onClick={onSave}
              shortcut="⌘S"
            />
            <IconButton 
              icon={Bug} 
              label="Debug" 
              onClick={onDebug}
              shortcut="⌘Shift+D"
            />
            <IconButton 
              icon={Wand2} 
              label="Fix with arcOS" 
              onClick={onFixWithARCOS}
              shortcut="⌘Alt+F"
            />
            <IconButton 
              icon={Terminal} 
              label="Toggle Terminal" 
              onClick={onTerminalToggle}
              shortcut="⌘`"
            />
            <div className="w-px h-4 bg-slate-600/30 mx-1" />
            <IconButton 
              icon={Undo2} 
              label="Undo" 
              onClick={onUndo}
              shortcut="⌘Z"
            />
            <IconButton 
              icon={Redo2} 
              label="Redo" 
              onClick={onRedo}
              shortcut="⌘Shift+Z"
            />
            <IconButton 
              icon={Settings} 
              label={layoutMode === 'classic' ? 'Hive Grid' : 'Classic'}
              onClick={onLayoutToggle}
            />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default TopBar;
